/**
 * Shared primitive schemas used across the content-schema surface.
 *
 * @remarks
 * The narrowest DRY layer of the package (engineering_standards §13.1): every
 * authored text field, counter, and PRNG-adjacent number funnels through one
 * of these three schemas so their validation semantics are defined exactly
 * once. Nothing here is content-specific.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

/** Matches any string containing at least one non-whitespace character. */
const NON_BLANK_PATTERN = /\S/u;

/**
 * A string with at least one non-whitespace character — the baseline for
 * every authored text field (names, descriptions, narrative responses).
 */
export const nonBlankStringSchema = z
	.string()
	.regex(NON_BLANK_PATTERN, 'Expected a non-blank string (at least one non-whitespace character)');

/** A non-negative integer — turn counts, draw counts, sequence counters. */
export const nonNegativeIntegerSchema = z.int().min(0);

/** A counting number (R12: `occurrence_tally_at_least.minimumCount` — a threshold of zero gates nothing). */
export const positiveIntegerSchema = z.int().min(1);

/**
 * Whose fact a piece of state records (D146, Mike's ruling: some state is
 * the player's, not the world's; D152 made it a primitive because a second
 * declaration wanted the same two words).
 *
 * @remarks
 * A `world` fact is about the facility — a door thrown, a clock running —
 * and the checkpoint puts it back with the rest of the world (D142). A
 * `player` fact is one the player carries — a frame on the film once seen, a
 * strap once broken — and it survives the checkpoint the way the hands do.
 * Each declaring field chooses its own default; most facts are the world's,
 * and a fact that should persist is the one worth saying out loud.
 */
export const factScopeSchema = z.enum(['player', 'world']);

/** Whose fact — see {@link factScopeSchema}. */
export type FactScope = z.infer<typeof factScopeSchema>;

/**
 * A uint32 — the exact value domain of the mulberry32 PRNG state
 * (architecture.md §3.4, decision D08). Used for authored fixed seeds and the
 * live `RandomnessState.seed`.
 */
export const randomSeedSchema = z.uint32();
